import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  UserIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import axiosInstance from '../../utils/axios';
import DeviceIcon from '../../components/DeviceIcon';

// The vendor landing page: every device this company has been granted,
// with live status. Access is granted device by device by the owner in the
// DM Mobile app (Dealer Access / Dryer Manufacturer Access toggles).
const VendorDevices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/vendor/devices');
        setDevices(response.data.devices || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch devices:', err);
        setError('Failed to load your devices. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, []);

  const ownerLabel = (device) => {
    const owner = device.owners?.[0];
    if (!owner) return null;
    const name = `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
    return owner.farmName ? `${owner.farmName}${name ? ` — ${name}` : ''}` : name || null;
  };

  const filteredDevices = devices.filter((device) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      device.serial?.toLowerCase().includes(term) ||
      device.dryerModel?.toLowerCase().includes(term) ||
      device.dryerManufacturer?.name?.toLowerCase().includes(term) ||
      ownerLabel(device)?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Your Devices</h1>
        <p className="text-gray-600 mt-1">
          Dryer Master systems your company has been granted access to
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {devices.length > 0 && (
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by serial, name, owner..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {!error && devices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <DeviceIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-gray-700 font-medium mb-2">No devices shared with you yet</p>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Access is granted by the dryer owner in the DM Mobile app, under
            External Access on their device page. Once an owner turns on access
            for your company, the device appears here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredDevices.map((device) => (
            <button
              key={device.serial}
              onClick={() => navigate(`/vendor/devices/${device.serial}`)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-blue-300 transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-50 rounded-lg">
                    <DeviceIcon model={device.model} className="w-6 h-6 text-primary-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 font-mono">
                      {device.serial}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    device.isOnline
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      device.isOnline ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  ></span>
                  {device.isOnline ? 'Online' : 'Offline'}
                </span>
              </div>

              <div className="space-y-1.5 text-sm text-gray-600">
                {(device.dryerManufacturer || device.dryerModel) && (
                  <p>
                    <span className="text-gray-400">Dryer:</span>{' '}
                    {[device.dryerManufacturer?.name, device.dryerModel].filter(Boolean).join(' ')}
                  </p>
                )}
                {ownerLabel(device) && (
                  <p className="flex items-center gap-1.5">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    {ownerLabel(device)}
                  </p>
                )}
                {device.location?.formatted && (
                  <p className="flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    {device.location.formatted}
                  </p>
                )}
                {!device.isOnline && device.lastSessionOffline && (
                  <p className="text-xs text-gray-400">
                    Last seen {formatDistanceToNow(new Date(device.lastSessionOffline), { addSuffix: true })}
                  </p>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end text-blue-600 text-sm font-medium">
                View device
                <ChevronRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}

      {devices.length > 0 && filteredDevices.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          No devices match your search
        </div>
      )}
    </div>
  );
};

export default VendorDevices;
